import type { QueryClient } from "@tanstack/react-query";
import {
  createCustomDashboard,
  dashboardCreateInputSchema,
  deleteCustomDashboard,
  getCustomDashboard,
  listCustomDashboards,
  updateCustomDashboard,
} from "@tracify/shared";
import {
  type CreateDashboardInput,
  type Dashboard,
} from "@lib/types/dashboards";
import type { ResponsiveInsightLayouts } from "@lib/types/insights";
import { getCustomInsightsApiRequestOptions } from "@/lib/insights/custom-insights-api-client";
import {
  mapApiDashboardToDashboard,
  mapDashboardToApiUpsertInput,
  normalizeDashboardForUpsertInput,
} from "@/lib/insights/dashboard-mappers";
import {
  generateDashboardDefaultLayout,
  getDashboardLayouts,
  mergeDashboardLayoutsIntoTiles,
} from "@/lib/insights/dashboard-layouts";
import { GRID_CONFIG } from "@lib/types/insights";

export type UseDashboardsOptions = {
  enabled?: boolean;
};

export type UpdateDashboardMetadataInput = Partial<
  Pick<
    Dashboard,
    "title" | "description" | "defaultTimeRange" | "defaultAttributionPresetId"
  >
>;

export type UpdateDashboardLayoutsInput = ResponsiveInsightLayouts;

export type UpdateDashboardInsightInput = string;

export type ReorderDashboardInsightsInput = string[];

type MutationQueryRefetchType = "active" | "inactive" | "all" | "none";

type MutationQueryInvalidationOptions = {
  refetchType?: MutationQueryRefetchType;
};

const DEFAULT_MUTATION_QUERY_REFETCH_TYPE: MutationQueryRefetchType = "all";

export function getDashboardsQueryKey(organisationId: string | undefined) {
  return ["dashboards", organisationId] as const;
}

export function getDashboardQueryKey(
  organisationId: string | undefined,
  dashboardId: string | undefined
) {
  return ["dashboard", organisationId, dashboardId] as const;
}

export async function invalidateDashboardsListQuery(
  queryClient: QueryClient,
  organisationId: string | undefined,
  options: MutationQueryInvalidationOptions = {}
) {
  await queryClient.invalidateQueries({
    queryKey: getDashboardsQueryKey(organisationId),
    refetchType: options.refetchType ?? DEFAULT_MUTATION_QUERY_REFETCH_TYPE,
  });
}

export async function invalidateDashboardQueries(
  queryClient: QueryClient,
  organisationId: string | undefined,
  dashboardId: string,
  options: MutationQueryInvalidationOptions = {}
) {
  const refetchType =
    options.refetchType ?? DEFAULT_MUTATION_QUERY_REFETCH_TYPE;

  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: getDashboardQueryKey(organisationId, dashboardId),
      refetchType,
    }),
    queryClient.invalidateQueries({
      queryKey: getDashboardsQueryKey(organisationId),
      refetchType,
    }),
  ]);
}

export async function fetchDashboardsFromApi(
  organisationId: string
): Promise<Dashboard[]> {
  if (typeof window === "undefined") {
    return [];
  }

  const response = await listCustomDashboards(
    getCustomInsightsApiRequestOptions(organisationId)
  );

  return response.items.map((item) => mapApiDashboardToDashboard(item));
}

export async function fetchDashboardById(
  dashboardId: string,
  organisationId: string
): Promise<Dashboard | null> {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const dashboard = await getCustomDashboard(
      getCustomInsightsApiRequestOptions(organisationId),
      dashboardId
    );

    return mapApiDashboardToDashboard(dashboard);
  } catch (error) {
    console.error("Failed to fetch dashboard:", error);
    return null;
  }
}

export async function resolveDashboardFromCacheOrFetch(
  queryClient: QueryClient,
  organisationId: string,
  dashboardId: string
): Promise<Dashboard | null> {
  const cachedDashboard = queryClient.getQueryData<Dashboard>(
    getDashboardQueryKey(organisationId, dashboardId)
  );

  if (cachedDashboard) {
    return cachedDashboard;
  }

  const cachedDashboards = queryClient.getQueryData<Dashboard[]>(
    getDashboardsQueryKey(organisationId)
  );
  const cachedListMatch = cachedDashboards?.find(
    (dashboard) => dashboard.id === dashboardId
  );

  if (cachedListMatch) {
    return cachedListMatch;
  }

  return fetchDashboardById(dashboardId, organisationId);
}

function mapValidatedDashboardUpsertInput(
  dashboard: Pick<Dashboard, "title" | "description" | "tiles"> & {
    defaultTimeRange?: NonNullable<Dashboard["defaultTimeRange"]> | null;
    defaultAttributionPresetId?: Exclude<
      Dashboard["defaultAttributionPresetId"],
      undefined
    >;
  }
) {
  return dashboardCreateInputSchema.parse(
    mapDashboardToApiUpsertInput(normalizeDashboardForUpsertInput(dashboard))
  );
}

export async function updateDashboardOnServer(
  organisationId: string,
  dashboardId: string,
  dashboard: Dashboard
): Promise<Dashboard> {
  const apiDashboard = await updateCustomDashboard(
    getCustomInsightsApiRequestOptions(organisationId),
    dashboardId,
    mapValidatedDashboardUpsertInput(dashboard)
  );

  return mapApiDashboardToDashboard(apiDashboard);
}

export async function createDashboardOnServer(
  organisationId: string,
  input: CreateDashboardInput
): Promise<Dashboard> {
  const createdDashboard = await createCustomDashboard(
    getCustomInsightsApiRequestOptions(organisationId),
    mapValidatedDashboardUpsertInput(input)
  );

  return mapApiDashboardToDashboard(createdDashboard);
}

export async function deleteDashboardOnServer(
  organisationId: string,
  dashboardId: string
): Promise<string> {
  await deleteCustomDashboard(
    getCustomInsightsApiRequestOptions(organisationId),
    dashboardId
  );

  return dashboardId;
}

export function createDuplicatedDashboard(
  sourceDashboard: Dashboard
): CreateDashboardInput {
  return {
    title: `${sourceDashboard.title} (Copy)`,
    description: sourceDashboard.description ?? "",
    tiles: sourceDashboard.tiles.map((tile) => ({
      insightId: tile.insightId,
      layouts: {
        lg: { ...tile.layouts.lg },
        sm: { ...tile.layouts.sm },
      },
    })),
    ...(sourceDashboard.defaultTimeRange !== undefined
      ? {
          defaultTimeRange: {
            ...sourceDashboard.defaultTimeRange,
          },
        }
      : {}),
    ...(sourceDashboard.defaultAttributionPresetId !== undefined
      ? {
          defaultAttributionPresetId:
            sourceDashboard.defaultAttributionPresetId,
        }
      : {}),
  };
}

export function createDashboardWithAddedInsight(
  currentDashboard: Dashboard,
  insightId: string
): Dashboard {
  if (currentDashboard.tiles.some((tile) => tile.insightId === insightId)) {
    return currentDashboard;
  }

  const newLayouts = {
    lg: generateDashboardDefaultLayout(
      insightId,
      currentDashboard.tiles.map((tile) => tile.layouts.lg),
      GRID_CONFIG.cols.lg
    ),
    sm: generateDashboardDefaultLayout(
      insightId,
      currentDashboard.tiles.map((tile) => tile.layouts.sm),
      GRID_CONFIG.cols.sm
    ),
  };

  return {
    ...currentDashboard,
    tiles: [
      ...currentDashboard.tiles,
      {
        insightId,
        layouts: newLayouts,
      },
    ],
    updatedAt: Date.now(),
  };
}

export function createDashboardWithAddedInsights(
  currentDashboard: Dashboard,
  insightIds: string[]
): Dashboard {
  return insightIds.reduce(
    (dashboard, insightId) =>
      createDashboardWithAddedInsight(dashboard, insightId),
    currentDashboard
  );
}

export function createDashboardWithRemovedInsight(
  currentDashboard: Dashboard,
  insightId: string
): Dashboard {
  return {
    ...currentDashboard,
    tiles: currentDashboard.tiles.filter(
      (tile) => tile.insightId !== insightId
    ),
    updatedAt: Date.now(),
  };
}

export function createDashboardWithUpdatedLayouts(
  currentDashboard: Dashboard,
  newLayouts: ResponsiveInsightLayouts
): Dashboard {
  return {
    ...currentDashboard,
    tiles: mergeDashboardLayoutsIntoTiles(currentDashboard.tiles, newLayouts),
    updatedAt: Date.now(),
  };
}

export function createDashboardWithReorderedInsights(
  currentDashboard: Dashboard,
  newInsightIds: string[]
): Dashboard {
  const tilesById = new Map(
    currentDashboard.tiles.map((tile) => [tile.insightId, tile])
  );
  const reorderedTiles = newInsightIds
    .map((id) => tilesById.get(id))
    .filter((tile): tile is NonNullable<typeof tile> => tile !== undefined);

  return {
    ...currentDashboard,
    tiles: reorderedTiles,
    updatedAt: Date.now(),
  };
}

export function createDashboardWithUpdatedMetadata(
  currentDashboard: Dashboard,
  updates: UpdateDashboardMetadataInput
): Dashboard {
  return {
    ...currentDashboard,
    ...updates,
    tiles: currentDashboard.tiles,
    updatedAt: Date.now(),
  };
}

export function getDashboardLayoutsFromDashboard(
  dashboard: Dashboard | null | undefined
): ResponsiveInsightLayouts {
  return dashboard ? getDashboardLayouts(dashboard.tiles) : { lg: [], sm: [] };
}
